<script setup>
import ConfirmComponent from '@/components/ConfirmComponent.vue';
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, reactive, ref, watch } from 'vue';

const props = defineProps({
    reservationNo: {
        type: String,
        default: ''
    },
    contractType: {
        type: String,
        default: 'SHORT'
    }
});

const emit = defineEmits(['update']);
const showConfirmDialog = ref(false);
const toastComponentRef = ref(null);
const confirmAction = ref(null);
const dialogHeader = ref('');
const dialogMessage = ref('');
const dialogcomplete = ref('');
let isConfirming = false;
const isPopupVisible = ref(false);
const isEditing = ref(false);
const editingPaymentId = ref(0);
const canceledPaymentMethod = ref('');
const allReadonly = ref(false);

const today = new Date();
const formatToYyyyMmDd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
let formattedDate = formatToYyyyMmDd(today);
let currentTime = new Date().toTimeString().slice(0, 5);

const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const initialCreateData = {
    reservation_no: '',
    payment_date: formattedDate,
    payment_amount: '',
    payment_category: '',
    payment_category_name: '',
    payment_method: '',
    remark: ''
};
const createData = reactive({ ...initialCreateData });

const initialData = [
    {
        payment_item_id: '',
        oder_id: '',
        payment_amount: '',
        payment_data: '',
        payment_category: '',
        payment_category_name: '',
        payment_method: '',
        remark: '',
        payment_key: ''
    }
];
const paymentItemList = reactive([{ ...initialData }]);

const initialValidMessage = {
    payment_amount: '',
    payment_data: '',
    payment_category: '',
    payment_method: '',
    remark: ''
};
const validationMessage = reactive({ ...initialValidMessage });

const initialCancelData = {
    payment_item_id: '',
    use_fee: 'N',
    cancel_reason: '',
    refund_account_number: '',
    refund_bank_code: '',
    refund_customer_name: '',
    refund_customer_phone: ''
};
const cancelData = reactive({ ...initialCancelData });

const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};

const resetData = () => {
    Object.assign(createData, initialCreateData);
    Object.assign(paymentItemList, initialData);
    Object.assign(validationMessage, initialValidMessage);
    emit('update', structuredClone(initialData));
};

const setDetailData = (newVal) => {
    paymentItemList.splice(0, paymentItemList.length, ...newVal);
};

// 팝업 열기/닫기
const togglePopup = () => {
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

const handleAddPayment = async () => {
    const config = {
        method: 'post'
    };

    createData.reservation_no = props.reservationNo;

    try {
        const { execute } = useAxios('/api/payment/contract', config, {
            immediate: false,
            onSuccess(response) {
                console.log(response);
                toastComponentRef.value.showToast('success', '등록', '입금내역이 등록 되었습니다.');
                nextTick(() => {
                    Object.assign(createData, initialCreateData);
                    paymentItemList.splice(0, paymentItemList.length, ...response.data.data);
                });
            },
            onError(err) {
                console.log('페이먼트 에러', err.errorMessage);
                Object.assign(validationMessage, err.response?.data?.data);
                toastComponentRef.value.showToast('error', '등록 실패', err.errorMessage);
            }
        });
        await execute(createData);
    } catch (err) {
        console.log(err);
    }
};

const getPaymentItemDetail = async (id) => {
    const config = {
        method: 'get',
        params: {
            paymentItemId: id
        }
    };

    try {
        const { execute } = useAxios('/api/payment/contract', config, {
            immediate: false,
            onSuccess(response) {
                console.log(response);
                toastComponentRef.value.showToast('success', '조회', '입금내역 단건이 조회 되었습니다.');
                nextTick(() => {
                    Object.assign(createData, response.data.data);
                    console.log('입금내역 단건 조회', createData);
                });
            },
            onError(err) {
                toastComponentRef.value.showToast('error', '조회 실패', err.message);
            }
        });
        await execute();
    } catch (err) {
        console.log(err);
    }
};

const paymentCategoryList = computed(() => {
    const base = commonCode('PAYMENT_CATEGORY').value;
    console.log('결제카테고리', base);

    if (props.contractType === 'MID' || props.contractType === 'LONG') {
        return base.filter((item) => item.value !== 'RENT' && item.value !== 'DIFF');
    }

    if (props.contractType === 'SHORT') {
        const hasRent = paymentItemList.some((item) => item.payment_category === 'RENT');
        return base.filter((item) => item.value !== 'DIFF' && (!hasRent || item.value !== 'RENT'));
    }

    const hasRent = paymentItemList.some((item) => item.payment_category === 'RENT');
    return hasRent ? base.filter((item) => item.value !== 'RENT') : base;
});

const setDialogInfo = (action) => {
    confirmAction.value = action;
    dialogHeader.value = dialogInfo[action].header;
    dialogMessage.value = dialogInfo[action].message;
    dialogcomplete.value = dialogInfo[action].completemessage;
};

const dialogInfo = {
    update: {
        header: '수정',
        message: '입금내역을 수정하시겠습니까?',
        completemessage: '입금내역이 수정되었습니다.'
    },
    cancel: {
        header: '취소',
        message: '결제를 취소하시겠습니까?',
        completemessage: '결제가 취소되었습니다.'
    }
};

const handleConfirm = () => {
    if (isConfirming) return;
    isConfirming = true;

    let url = '';
    let config = null;
    let payload = null;

    switch (confirmAction.value) {
        case 'update':
            url = `/api/payment/contract`;
            config = {
                method: 'put',
                params: {
                    paymentItemId: editingPaymentId.value
                }
            };
            payload = createData;

            try {
                const { execute } = useAxios(`${url}`, config, {
                    immediate: false,
                    onSuccess(response) {
                        console.log(response);
                        confirmSuccess();
                        nextTick(() => {
                            isEditing.value = false;
                            editingPaymentId.value = 0;
                            Object.assign(createData, initialCreateData);
                            paymentItemList.splice(0, paymentItemList.length, ...response.data.data);
                        });
                    },
                    onError(err) {
                        toastComponentRef.value.showToast('error', '수정 실패', err.message);
                    }
                });
                execute(payload);
            } catch (err) {
                console.log(err);
            }
            break;
        case 'cancel':
            url = `/api/payment`;
            config = {
                method: 'delete'
            };
            payload = cancelData;

            try {
                const { execute } = useAxios(`${url}`, config, {
                    immediate: false,
                    onSuccess(response) {
                        console.log(response);
                        confirmSuccess();
                        nextTick(() => {
                            canceledPaymentMethod.value = '';
                            closePopup();
                            Object.assign(cancelData, initialCancelData);
                            paymentItemList.splice(0, paymentItemList.length, ...response.data.data);
                        });
                    },
                    onError(err) {
                        toastComponentRef.value.showToast('error', '취소 실패', err.message);
                    }
                });
                execute(payload);
            } catch (err) {
                console.log(err);
            }
            break;
    } // end switch
};

const confirmSuccess = () => {
    if (toastComponentRef.value) {
        toastComponentRef.value.showToast('success', dialogHeader.value, dialogcomplete.value);
    }
    showConfirmDialog.value = false;
    setTimeout(() => {
        isConfirming = false;
    }, 1000);
};

const handleCancel = () => {
    if (isConfirming) return;
    showConfirmDialog.value = false;
};

const handleEditModeEnable = (id) => {
    isEditing.value = true;
    editingPaymentId.value = id;

    nextTick(() => {
        getPaymentItemDetail(id);
    });
};

const handleEditModeDisable = () => {
    isEditing.value = false;
    editingPaymentId.value = 0;

    nextTick(() => {
        Object.assign(createData, initialCreateData);
    });
};

const handleUpdatePayment = () => {
    setDialogInfo('update');
    showConfirmDialog.value = true;
};

const handleOpenCancelPopup = (method, id) => {
    togglePopup();
    canceledPaymentMethod.value = method;
    cancelData.payment_item_id = id;
};

const handelCloseCancelPopup = () => {
    closePopup();
    canceledPaymentMethod.value = '';

    nextTick(() => {
        Object.assign(cancelData, initialCancelData);
    });
};

const handleCancelPayment = () => {
    setDialogInfo('cancel');
    showConfirmDialog.value = true;
};

const updateInputValue = (inputId, val) => {
    console.log('들어온값 ', val);
    console.log('값의 id ', inputId);

    switch (inputId) {
        case 'payment_category':
            const PaymentCategoryList = commonCode('PAYMENT_CATEGORY').value;
            createData.payment_category_name = PaymentCategoryList.find((o) => o.value === val.value)?.text ?? '';
    }
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return '0';
    }
    return Number(value).toLocaleString();
};

// onMounted(() => {
//     createData.reservation_no = String(reservationNoRef.value ?? '');
// });

watch(
    () => props.reservationNo,
    (newVal) => {
        createData.reservation_no = newVal ?? '';
    },
    { immediate: true }
);

defineExpose({
    resetData,
    setAllReadonly,
    setDetailData
});
</script>
<template>
    <div class="section-box base-text">
        <ToastComponent ref="toastComponentRef" />
        <ConfirmComponent
            v-model:visible="showConfirmDialog"
            :dialogHeader="dialogHeader"
            :dialogMessage="dialogMessage"
            dialogIcon="pi pi-exclamation-triangle"
            dialogWidth="400px"
            dialogButtonSeverity="danger"
            @confirm="handleConfirm"
            @cancel="handleCancel"
        />
        <h2 class="section-title mb-2 z-[-10]">입금 내역</h2>
        <div class="section-body">
            <div class="section-left">
                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <DatePickerWithRegistComponent2 :required="true" :readonly="allReadonly" inputId="payment_data" v-model="createData.payment_date" :errmsg="validationMessage.payment_data" label="입금일자" />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="payment_amount"
                            :number="'n'"
                            :required="true"
                            :readonly="allReadonly"
                            :placeholder="'입금액을 입력하세요.'"
                            :unit="'원'"
                            v-model="createData.payment_amount"
                            :errmsg="validationMessage.payment_amount"
                            :padding="'0.5rem 2.5rem 0.5rem 0.5rem'"
                            label="입금액"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="payment_category"
                            :required="true"
                            :readonly="allReadonly"
                            :options="paymentCategoryList"
                            :padding="'0.5rem'"
                            v-model="createData.payment_category"
                            :errmsg="validationMessage.payment_category"
                            label="결제항목"
                            @change="(val) => updateInputValue('payment_category', val)"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="payment_method"
                            :required="true"
                            :readonly="allReadonly"
                            :options="commonCode('PAYMENT_METHOD').value"
                            :padding="'0.5rem'"
                            v-model="createData.payment_method"
                            :errmsg="validationMessage.payment_method"
                            label="결제방법"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[100%]">
                        <customtextbox input="remark" v-model="createData.remark" :readonly="allReadonly" :height="'100px'" placeholder="결제 메모를 입력해주세요." label="empty" />
                    </div>
                </div>
                <div v-if="!isEditing" class="flex justify-end items-end mt-3">
                    <button
                        :disabled="allReadonly"
                        :class="[
                            'h-[2.3rem] w-[7rem] border font-semibold text-[0.9rem] rounded-[0.4rem]',
                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                        ]"
                        @click="handleAddPayment"
                    >
                        입금내역 입력
                    </button>
                </div>
                <div v-else class="flex justify-end items-end mt-3 gap-2">
                    <button
                        :disabled="allReadonly"
                        :class="[
                            'h-[2.3rem] w-[7rem] border font-semibold text-[0.9rem] rounded-[0.4rem]',
                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                        ]"
                        @click="handleUpdatePayment"
                    >
                        입금내역 수정
                    </button>
                    <button
                        :disabled="allReadonly"
                        :class="[
                            'h-[2.3rem] w-[5rem] border font-semibold text-[0.9rem] rounded-[0.4rem]',
                            allReadonly ? 'bg-[#f5f5f5] border-[#d1d5db] text-[#9a9ca0] cursor-default' : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150'
                        ]"
                        @click="handleEditModeDisable()"
                    >
                        취소
                    </button>
                </div>
            </div>
            <div class="section-right">
                <div class="xxl:mt-9 mt-4">
                    <table>
                        <thead>
                            <tr>
                                <th>입금일자</th>
                                <th>입금금액</th>
                                <th>결제항목</th>
                                <th>결제방법</th>
                                <th>결제메모</th>
                                <th>비고</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!paymentItemList.length">
                                <td colspan="5">입금 내역이 없습니다</td>
                            </tr>
                            <tr v-else v-for="paymentItem in paymentItemList" :key="paymentItem.payment_item_id" :class="paymentItem.payment_item_id === editingPaymentId ? 'bg-[#F0F3FF] hover:bg-[#F0F3FF]' : 'bg-white hover:bg-[#fafafa]'">
                                <td>{{ paymentItem.payment_date }}</td>
                                <td style="text-align: right">{{ formatCurrency(paymentItem.payment_amount) }} 원</td>
                                <td>{{ paymentItem.payment_category_name }}</td>
                                <td>{{ commonCode('PAYMENT_METHOD').value.find((o) => o.value === paymentItem.payment_method)?.text ?? '' }}</td>
                                <td :title="paymentItem.remark" class="max-w-[100px] whitespace-nowrap overflow-hidden text-ellipsis text-left">{{ paymentItem.remark }}</td>
                                <td>
                                    <div>
                                        <button
                                            v-show="!paymentItem.payment_key"
                                            :disabled="paymentItem.payment_item_id === editingPaymentId"
                                            class="h-[1.8rem] w-[4rem] bg-[#f1f1f1] text-[#4b5563] hover:bg-[#dddee0] active:bg-[#dddee0] disabled:bg-[#7A7A7A] disabled:text-[white] text-[0.815rem] rounded-[0.4rem]"
                                            @click="handleEditModeEnable(paymentItem.payment_item_id)"
                                        >
                                            <span v-if="paymentItem.payment_item_id !== editingPaymentId">수정</span>
                                            <span v-else>수정중</span>
                                        </button>
                                        <button
                                            v-show="paymentItem.order_payment_status === 'DONE'"
                                            class="h-[1.8rem] w-[4rem] bg-[#f1f1f1] text-[#4b5563] hover:bg-[#dddee0] active:bg-[#dddee0] text-[0.815rem] rounded-[0.4rem]"
                                            @click="handleOpenCancelPopup(paymentItem.payment_method, paymentItem.payment_item_id)"
                                        >
                                            결제취소
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <div>
                        <div v-show="canceledPaymentMethod === 'VIRTUAL_ACCOUNT'">
                            <div class="section-row">
                                <div class="input-box w-[50%]">
                                    <inputbox inputId="refund_account_number" :required="true" v-model="cancelData.refund_account_number" :placeholder="'환불계좌번호를 입력하세요.'" label="환불계좌번호" />
                                </div>
                                <div class="input-box w-[50%]">
                                    <combobox inputId="refund_bank_code" :required="true" v-model="cancelData.refund_bank_code" :options="commonCode('BANK_CODE').value" :padding="'0.5rem'" label="환불계좌 은행" />
                                </div>
                            </div>
                            <div class="section-row">
                                <div class="input-box w-[50%]">
                                    <inputbox inputId="refund_customer_name" :required="true" v-model="cancelData.refund_customer_name" :placeholder="'예금주명을 입력하세요.'" label="환불계좌 예금주명" />
                                </div>
                                <div class="input-box w-[50%]">
                                    <inputbox inputId="refund_customer_phone" :required="true" v-model="cancelData.refund_customer_phone" :placeholder="'예금주 연락처를 입력하세요.'" label="환불계좌 예금주 연락처" />
                                </div>
                            </div>
                        </div>
                        <div class="section-row">
                            <div class="input-box w-[100%]">
                                <customtextbox input="cancel_reason" v-model="cancelData.cancel_reason" :height="'100px'" placeholder="취소 사유를 입력하세요." label="취소 사유" />
                            </div>
                        </div>
                        <div class="section-row">
                            <div class="input-box w-[100%] flex justify-end">
                                <togglebox inputId="is_reservation" v-model="cancelData.use_fee" spanText="수수료 계산" label="" />
                            </div>
                        </div>
                    </div>
                    <button @click="handleCancelPayment" class="cancel-button">결제 취소</button>
                    <button @click="handelCloseCancelPopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
<style scoped>
table {
    width: 100%;
    border-collapse: collapse;
}

table thead {
    display: table;
    width: 100%;
    color: #35424a;
    font-size: 0.875rem;
    background-color: #f5f5f5;
    border-top: 1px solid #83b9ff;
    table-layout: fixed;
}

table thead th {
    padding: 0.5rem;
}

table tbody {
    display: block;
    max-height: 225px;
    overflow-y: auto;
    width: 100%;
}

table tbody tr {
    display: table;
    width: 100%;
    border-bottom: 0.5px solid #dcdcdc;
    table-layout: fixed;
}

table tbody td {
    text-align: center;
    font-size: 0.875rem;
    padding: 0.5rem;
    position: relative;
}

table tbody td::after {
    content: '';
    width: 0.5px;
    height: 2rem;
    background-color: #ebebeb;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}

table tbody td:last-of-type::after {
    content: none;
}

/* 크롬, 엣지, 사파리 */
tbody::-webkit-scrollbar {
    width: 3px; /* 아주 얇게 */
}

tbody::-webkit-scrollbar-track {
    background: transparent; /* 트랙 숨김 */
}

tbody::-webkit-scrollbar-thumb {
    background-color: #ccc; /* 색상 */
    border-radius: 2px;
}

/* 팝업 */
.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
}

.popup-content {
    background: #fff;
    padding: 1rem;
    /* padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem; */
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 40rem;
    max-height: 80vh;
    text-align: left;
    min-width: 30rem;
    display: flex;
    flex-direction: column;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 0.5rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.4rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.cancel-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    color: #ef4444;
    margin-top: 1rem;
    background-color: #fef2f2;
    border: 1px solid #ef4444;
    padding: 0.5rem 1rem;
    /* border: 1px solid #d6d6d6; */
    border-radius: 0.4rem;
    cursor: pointer;
}

.cancel-button:hover {
    background: #fee2e2;
}
</style>
